import { SafeReportResponse } from "../Conversion";
import { formatAbbreviatedMoney, formatExactMoneyDollars } from "../Money";
import * as client from "../../../client";
import { DBPerilType } from "../general-input/util";

export const PERIL_TYPE_ORDER: DBPerilType[] = [
  "aop",
  "named_storm",
  "earthquake",
  "earth_movement",
  "flood",
  "wildfire",
  "scs",
  "wind_and_hail",
  "equipment_breakdown",
];

const PERIL_CODE_DISPLAY_NAMES: Record<string, string> = {
  PWH: "Hurricane - Wind Only",
  PES: "Earthquake - Ground Shake",
  PFL: "Inland Flood",
  PWB: "Wildfire",
  PWW: "Winter Storm",
  PWT: "Severe Thunderstorm",
  AOP: "All Other Perils",
  EBP: "Equipment Breakdown",
};

export const perilCodeDisplayName = (code: string): string =>
  PERIL_CODE_DISPLAY_NAMES[code] ?? code;

export const compareLayers = (a: client.Layer, b: client.Layer): number => {
  // 1. Primary (starting_point=0) before excess
  const aIsPrimary = a.starting_point === 0 ? 0 : 1;
  const bIsPrimary = b.starting_point === 0 ? 0 : 1;
  if (aIsPrimary !== bIsPrimary) return aIsPrimary - bIsPrimary;

  // 2. By starting point ascending
  if (a.starting_point !== b.starting_point)
    return a.starting_point - b.starting_point;

  // 3. By size (range) ascending
  return a.range - b.range;
};

export const sortedLayersFromReport = (
  specialtyPropertyInfo: SafeReportResponse
) => specialtyPropertyInfo.report_json.layer_info.sort(compareLayers);

export const readableLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;
  const isPartial =
    !!participation && participation > 0 && participation < range;
  const base =
    starting_point === 0
      ? `Primary ${formatAbbreviatedMoney(range)}`
      : `${formatAbbreviatedMoney(range)} xs ${formatAbbreviatedMoney(
          starting_point
        )}`;
  return isPartial
    ? `${formatAbbreviatedMoney(participation)} part of ${base}`
    : base;
};

export const readableLimitOfLiabilityFromLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;

  const effectiveParticipation = participation ?? range;
  const hasExcess = starting_point > 0;
  const isPartial = effectiveParticipation < range;

  let result = "";

  if (isPartial && hasExcess) {
    // e.g. $10M part of $25M x/s $25M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)} excess ${formatExactMoneyDollars(starting_point)}`;
  } else if (isPartial) {
    // e.g. $5M part of $10M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)}`;
  } else if (hasExcess) {
    // e.g. $5M x/s $5M per occurrence
    result = `${formatExactMoneyDollars(range)} excess ${formatExactMoneyDollars(starting_point)}`;
  } else {
    // e.g. $10M per occurrence
    result = formatExactMoneyDollars(range);
  }

  return `${result} per occurrence`;
};

export function hasOverlappingLayers(layers: client.Layer[]): boolean {
  return layers.some((a, i) =>
    layers.slice(i + 1).some((b) => {
      const aEnd = a.starting_point + a.range;
      const bEnd = b.starting_point + b.range;
      return a.starting_point < bEnd && aEnd > b.starting_point;
    })
  );
}

/** Soft floor for the AAL multiple. Edits (to the Multiple or the Layer Prem)
 * that would push the multiple below this prompt for confirmation rather than
 * being blocked outright. */
export const MIN_MULTIPLE = 1.33;

/** Peril types included in the layer loss ratio and total AAL: the standalone
 * cat perils. The "aop" peril type is excluded — it bundles the All Other
 * Perils direct premium with the (modeled) Winter Storm AAL, and we report the
 * loss ratio against the standalone cat perils only. Equipment breakdown is
 * likewise excluded. */
export const isLossRatioPeril = (peril: DBPerilType): boolean =>
  peril !== "aop" && peril !== "equipment_breakdown";
